import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { useSearchParams } from 'react-router-dom';
import { Layout } from '../../components/Layout';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../../components/ui/card';
import { Badge } from '../../components/ui/badge';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from '../../components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../../components/ui/tabs';
import { Switch } from '../../components/ui/switch';
import { ScrollArea } from '../../components/ui/scroll-area';
import { Separator } from '../../components/ui/separator';
import { Textarea } from '../../components/ui/textarea';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../../components/ui/table';
import { 
  Phone, PhoneIncoming, PhoneOff, PhoneCall, Users, Clock, Calendar, 
  RefreshCw, Play, Pause, UserCheck, UserX, AlertCircle, CheckCircle,
  Headphones, Radio, Activity, Settings, Plus, ChevronRight, Zap,
  ChevronLeft, Filter, ArrowRight, Crown, Timer, LayoutDashboard,
  PhoneMissed, FileText, ScrollText, StickyNote, Search, Edit,
  Trash2, Archive, Eye, ExternalLink, MoreHorizontal, Bell, X, Monitor,
  Bot, Shield
} from 'lucide-react';
import { useAuth } from '../../contexts/AuthContext';
import { toast } from 'sonner';
import axios from 'axios';
import { Link } from 'react-router-dom';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Status Colors and Labels
const STATUS_COLORS = {
  available: 'bg-green-500',
  on_call: 'bg-blue-500',
  wrap_up: 'bg-yellow-500',
  break: 'bg-orange-500',
  unavailable: 'bg-red-500',
  offline: 'bg-gray-500',
};

const STATUS_LABELS = {
  available: 'Available',
  on_call: 'On Call',
  wrap_up: 'Wrap Up',
  break: 'On Break',
  unavailable: 'Unavailable',
  offline: 'Offline',
};

const STATUS_BUTTONS = [
  { status: 'available', label: 'Available', icon: UserCheck, color: 'bg-green-600 hover:bg-green-700' },
  { status: 'break', label: 'Break', icon: Pause, color: 'bg-orange-500 hover:bg-orange-600' },
  { status: 'offline', label: 'Offline', icon: UserX, color: 'bg-gray-500 hover:bg-gray-600' },
];

const SIMULATION_STATES = {
  idle: { label: 'Idle', color: 'bg-gray-400' },
  ringing: { label: 'Ringing', color: 'bg-yellow-500 animate-pulse' },
  connected: { label: 'Connected', color: 'bg-green-500' },
  wrap_up: { label: 'Wrap Up', color: 'bg-orange-500' },
};

// Tab definitions
const TABS = [
  { id: 'dashboard', label: 'Dashboard', icon: LayoutDashboard },
  { id: 'time-slots', label: 'Time Slots', icon: Calendar },
  { id: 'callbacks', label: 'Callbacks', icon: PhoneIncoming },
  { id: 'call-log', label: 'Call Log', icon: ScrollText },
  { id: 'scripts', label: 'Scripts', icon: FileText },
];

// Helper functions
const formatTime = (dateStr) => {
  if (!dateStr) return '';
  const d = new Date(dateStr);
  return d.toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit', hour12: true });
};

const formatDate = (dateStr) => {
  const d = new Date(dateStr);
  return d.toLocaleDateString('en-US', { weekday: 'short', month: 'short', day: 'numeric' });
};

const formatDateTime = (dateStr) => {
  if (!dateStr) return '';
  const d = new Date(dateStr);
  return d.toLocaleString('en-US', { 
    month: 'short', day: 'numeric', 
    hour: 'numeric', minute: '2-digit', hour12: true 
  });
};

const getNext7Days = () => {
  const days = [];
  const today = new Date();
  for (let i = 0; i < 8; i++) {
    const d = new Date(today);
    d.setDate(today.getDate() + i);
    days.push({
      date: d.toISOString().split('T')[0],
      label: i === 0 ? 'Today' : i === 1 ? 'Tomorrow' : formatDate(d),
      dayName: d.toLocaleDateString('en-US', { weekday: 'short' }),
    });
  }
  return days;
};

// ==========================================
// Rep Card Component
// ==========================================
const RepCard = ({ rep, onStatusChange, isNextInQueue }) => {
  const statusColor = STATUS_COLORS[rep.status] || 'bg-gray-500';
  const simState = SIMULATION_STATES[rep.simulation_state] || SIMULATION_STATES.idle;
  
  const handleStatusClick = (newStatus) => {
    if (rep.status === 'on_call') {
      toast.error('Cannot change status while on call');
      return;
    }
    onStatusChange(rep.user_id, newStatus);
  };
  
  return (
    <Card className={`relative overflow-hidden ${isNextInQueue ? 'ring-2 ring-green-500' : ''}`}>
      {isNextInQueue && (
        <div className="absolute top-0 right-0 bg-green-500 text-white text-xs px-2 py-0.5 rounded-bl flex items-center gap-1">
          <Crown className="h-3 w-3" />
          Next
        </div>
      )}
      
      <CardContent className="p-4">
        <div className="flex items-center justify-between mb-3">
          <div className="flex items-center gap-3">
            <div className="relative">
              <div className="w-10 h-10 rounded-full bg-muted flex items-center justify-center">
                <Headphones className="h-5 w-5 text-muted-foreground" />
              </div>
              <div className={`absolute -bottom-0.5 -right-0.5 w-3.5 h-3.5 rounded-full border-2 border-background ${statusColor}`} />
            </div>
            <div>
              <h4 className="font-medium text-sm">{rep.user_name}</h4>
              <p className="text-xs text-muted-foreground">{rep.email}</p>
            </div>
          </div>
          <div className="flex flex-col items-end gap-1">
            <Badge variant="outline" className={`${simState.color} text-white border-0 text-xs`}>
              {simState.label}
            </Badge>
            {rep.workstation_open && (
              <Badge variant="outline" className="bg-purple-500/20 text-purple-400 border-purple-500/30 text-xs">
                <Monitor className="h-3 w-3 mr-1" />
                Work Station
              </Badge>
            )}
          </div>
        </div>
        
        {rep.current_call_id && (
          <div className="bg-blue-500/10 rounded-lg p-2 mb-3 flex items-center gap-2">
            <PhoneCall className="h-4 w-4 text-blue-500 animate-pulse" />
            <span className="text-xs text-blue-400">Active Call</span>
            <span className="text-xs font-mono text-muted-foreground ml-auto">
              {rep.current_call_id.slice(0, 8)}...
            </span>
          </div>
        )}
        
        <div className="flex items-center justify-between text-xs text-muted-foreground mb-3">
          <span>Calls Today: {rep.calls_handled_today || 0}</span>
        </div>
        
        <div className="flex gap-1">
          {STATUS_BUTTONS.map(({ status, label, icon: Icon, color }) => (
            <Button
              key={status}
              size="sm"
              variant={rep.status === status ? 'default' : 'outline'}
              className={`flex-1 text-xs h-8 ${rep.status === status ? color + ' text-white' : ''}`}
              onClick={() => handleStatusClick(status)}
              disabled={rep.status === 'on_call'}
            >
              <Icon className="h-3 w-3 mr-1" />
              {label}
            </Button>
          ))}
        </div>
      </CardContent>
    </Card>
  );
};

// ==========================================
// Slot Card Component
// ==========================================
const SlotCard = ({ slot, onSimulate }) => {
  const isFull = slot.capacity_available === 0;
  const utilization = slot.capacity_total > 0 ? (slot.capacity_used / slot.capacity_total) * 100 : 0;
  
  return (
    <Card className={`${isFull ? 'opacity-60' : ''}`}>
      <CardContent className="p-3">
        <div className="flex items-center justify-between mb-2">
          <div className="flex items-center gap-2">
            <Clock className="h-4 w-4 text-muted-foreground" />
            <span className="font-medium text-sm">
              {formatTime(slot.slot_start)} - {formatTime(slot.slot_end)}
            </span>
          </div>
          {isFull && (
            <Badge variant="destructive" className="text-xs">Full</Badge>
          )}
        </div>
        
        <div className="mb-2">
          <div className="flex justify-between text-xs text-muted-foreground mb-1">
            <span>Capacity</span>
            <span>{slot.capacity_used}/{slot.capacity_total}</span>
          </div>
          <div className="h-2 bg-muted rounded-full overflow-hidden">
            <div 
              className={`h-full transition-all ${
                utilization >= 100 ? 'bg-red-500' : 
                utilization >= 75 ? 'bg-yellow-500' : 'bg-green-500'
              }`}
              style={{ width: `${Math.min(utilization, 100)}%` }}
            />
          </div>
        </div>
        
        <div className="flex items-center justify-between text-xs">
          <span className="text-muted-foreground">
            {slot.capacity_available} available
          </span>
          {!isFull && onSimulate && (
            <Button 
              size="sm" 
              variant="ghost" 
              className="h-6 text-xs"
              onClick={() => onSimulate(slot)}
            >
              <Zap className="h-3 w-3 mr-1" />
              Test
            </Button>
          )}
        </div>
      </CardContent>
    </Card>
  );
};

// ==========================================
// Active Call Card Component
// ==========================================
const ActiveCallCard = ({ call, onComplete }) => {
  const [duration, setDuration] = useState(0);
  
  useEffect(() => {
    if (call.call_started_at) {
      const startTime = new Date(call.call_started_at).getTime();
      const interval = setInterval(() => {
        setDuration(Math.floor((Date.now() - startTime) / 1000));
      }, 1000);
      return () => clearInterval(interval);
    }
  }, [call.call_started_at]);
  
  const formatDuration = (secs) => {
    const mins = Math.floor(secs / 60);
    const s = secs % 60;
    return `${mins}:${s.toString().padStart(2, '0')}`;
  };
  
  return (
    <Card className="bg-blue-500/5 border-blue-500/20">
      <CardContent className="p-3">
        <div className="flex items-center justify-between mb-2">
          <div className="flex items-center gap-2">
            <div className="w-2 h-2 rounded-full bg-green-500 animate-pulse" />
            <span className="font-medium text-sm">{call.customer_name || 'Unknown'}</span>
          </div>
          <Badge variant="outline" className="bg-blue-500/20 text-blue-400 border-blue-500/30 text-xs">
            in_progress
          </Badge>
        </div>
        
        <div className="space-y-1 text-xs text-muted-foreground">
          <div className="flex items-center gap-2">
            <Phone className="h-3 w-3" />
            <span>{call.phone}</span>
          </div>
          <div className="flex items-center gap-2">
            <Users className="h-3 w-3" />
            <span>Rep: {call.assigned_rep_name || 'Unassigned'}</span>
          </div>
          <div className="flex items-center gap-2">
            <Timer className="h-3 w-3" />
            <span>Duration: {formatDuration(duration)}</span>
          </div>
        </div>
        
        {onComplete && (
          <Button 
            size="sm" 
            variant="outline" 
            className="w-full mt-2 h-7 text-xs"
            onClick={() => onComplete(call.id)}
          >
            <CheckCircle className="h-3 w-3 mr-1" />
            Complete Call
          </Button>
        )}
      </CardContent>
    </Card>
  );
};

// ==========================================
// Simulate Call Dialog
// ==========================================
const SimulateCallDialog = ({ open, onOpenChange, slots, onSimulate }) => {
  const [selectedSlot, setSelectedSlot] = useState('now');
  const [callCount, setCallCount] = useState('1');
  const [isSimulating, setIsSimulating] = useState(false);
  const [results, setResults] = useState([]);
  
  const handleSimulate = async () => {
    setIsSimulating(true);
    setResults([]);
    
    const count = parseInt(callCount);
    const newResults = [];
    
    for (let i = 0; i < count; i++) {
      try {
        const response = await axios.post(`${API}/discovery/simulate-call`, {
          customer_name: `Test Inbound ${i + 1}`,
          phone: `(555) ${String(Math.floor(Math.random() * 900) + 100)}-${String(Math.floor(Math.random() * 9000) + 1000)}`,
          desired_service: 'Discovery Call Test'
        });
        
        newResults.push({
          success: response.data.success,
          customer: `Test Inbound ${i + 1}`,
          rep: response.data.routing?.assigned_rep_name || 'No rep available',
          message: response.data.message
        });
      } catch (error) {
        newResults.push({
          success: false,
          customer: `Test Inbound ${i + 1}`,
          rep: 'Error',
          message: error.response?.data?.detail || 'Simulation failed'
        });
      }
      
      if (i < count - 1) {
        await new Promise(r => setTimeout(r, 500));
      }
    }
    
    setResults(newResults);
    setIsSimulating(false);
    onSimulate?.();
  };
  
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Zap className="h-5 w-5 text-yellow-500" />
            Simulate Inbound Calls
          </DialogTitle>
          <DialogDescription>
            Test round-robin routing by simulating inbound discovery calls
          </DialogDescription>
        </DialogHeader>
        
        <div className="space-y-4 py-4">
          <div className="space-y-2">
            <Label>Slot Timing</Label>
            <Select value={selectedSlot} onValueChange={setSelectedSlot}>
              <SelectTrigger>
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="now">Now (Immediate)</SelectItem>
                {slots?.slice(0, 5).map((slot, i) => (
                  <SelectItem key={i} value={slot.slot_start}>
                    {formatTime(slot.slot_start)} - {formatTime(slot.slot_end)}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          
          <div className="space-y-2">
            <Label>Number of Simultaneous Calls</Label>
            <Select value={callCount} onValueChange={setCallCount}>
              <SelectTrigger>
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="1">1 call</SelectItem>
                <SelectItem value="2">2 calls</SelectItem>
                <SelectItem value="3">3 calls</SelectItem>
              </SelectContent>
            </Select>
          </div>
          
          {results.length > 0 && (
            <div className="space-y-2">
              <Label>Routing Results</Label>
              <div className="space-y-2 max-h-48 overflow-y-auto">
                {results.map((result, i) => (
                  <div 
                    key={i} 
                    className={`p-2 rounded-lg text-sm flex items-center justify-between ${
                      result.success ? 'bg-green-500/10 text-green-400' : 'bg-red-500/10 text-red-400'
                    }`}
                  >
                    <span>{result.customer}</span>
                    <span className="flex items-center gap-1">
                      <ArrowRight className="h-3 w-3" />
                      {result.rep}
                    </span>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>
        
        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>
            Close
          </Button>
          <Button onClick={handleSimulate} disabled={isSimulating}>
            {isSimulating ? (
              <>
                <RefreshCw className="h-4 w-4 mr-2 animate-spin" />
                Simulating...
              </>
            ) : (
              <>
                <Play className="h-4 w-4 mr-2" />
                Run Simulation
              </>
            )}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

// ==========================================
// Dashboard Tab Content
// ==========================================
const DashboardTabContent = ({ stats, activeCalls, onCompleteCall, aiAnswerStats }) => {
  return (
    <div className="space-y-4">
      {/* Overview Stats */}
      <div className="grid grid-cols-4 gap-4">
        <Card className="bg-gradient-to-br from-blue-500/10 to-blue-600/5">
          <CardContent className="p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-muted-foreground">Active Now</p>
                <p className="text-3xl font-bold text-blue-500">{stats.on_call_reps}</p>
              </div>
              <PhoneCall className="h-10 w-10 text-blue-500/30" />
            </div>
          </CardContent>
        </Card>
        
        <Card className="bg-gradient-to-br from-green-500/10 to-green-600/5">
          <CardContent className="p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-muted-foreground">Completed Today</p>
                <p className="text-3xl font-bold text-green-500">{stats.completed_calls_today}</p>
              </div>
              <CheckCircle className="h-10 w-10 text-green-500/30" />
            </div>
          </CardContent>
        </Card>
        
        <Card className="bg-gradient-to-br from-yellow-500/10 to-yellow-600/5">
          <CardContent className="p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-muted-foreground">Booked</p>
                <p className="text-3xl font-bold text-yellow-500">{stats.booked_slots_today}</p>
              </div>
              <Calendar className="h-10 w-10 text-yellow-500/30" />
            </div>
          </CardContent>
        </Card>
        
        <Card className="bg-gradient-to-br from-purple-500/10 to-purple-600/5">
          <CardContent className="p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-muted-foreground">Avg Duration</p>
                <p className="text-3xl font-bold text-purple-500">4:32</p>
              </div>
              <Timer className="h-10 w-10 text-purple-500/30" />
            </div>
          </CardContent>
        </Card>
      </div>

      {/* AI Answer Agents Status Section */}
      {aiAnswerStats && (
        <Card className="border-cyan-500/20">
          <CardHeader className="pb-3">
            <div className="flex items-center justify-between">
              <CardTitle className="text-lg flex items-center gap-2">
                <Bot className="h-5 w-5 text-cyan-500" />
                AI Answer Agents
                <Badge 
                  variant="outline" 
                  className={`ml-2 ${
                    aiAnswerStats.routing?.ai_overflow_enabled 
                      ? 'bg-green-500/10 text-green-400 border-green-500/30' 
                      : 'bg-gray-500/10 text-gray-400 border-gray-500/30'
                  }`}
                >
                  {aiAnswerStats.routing?.ai_overflow_enabled ? 'Active' : 'Disabled'}
                </Badge>
              </CardTitle>
              <Link to="/call-center/ai-answer-settings">
                <Button variant="outline" size="sm" className="gap-2">
                  <Settings className="h-4 w-4" />
                  Configure
                </Button>
              </Link>
            </div>
            <CardDescription>
              Backup AI agents handle overflow when human agents are unavailable
            </CardDescription>
          </CardHeader>
          <CardContent>
            <div className="grid grid-cols-4 gap-4">
              <div className="p-3 rounded-lg bg-muted/50 text-center">
                <div className="text-2xl font-bold text-cyan-500">{aiAnswerStats.agents?.enabled || 0}</div>
                <div className="text-xs text-muted-foreground">Enabled</div>
              </div>
              <div className="p-3 rounded-lg bg-muted/50 text-center">
                <div className="text-2xl font-bold text-green-500">{aiAnswerStats.agents?.available || 0}</div>
                <div className="text-xs text-muted-foreground">Available</div>
              </div>
              <div className="p-3 rounded-lg bg-muted/50 text-center">
                <div className="text-2xl font-bold text-blue-500">{aiAnswerStats.agents?.on_call || 0}</div>
                <div className="text-xs text-muted-foreground">On Call</div>
              </div>
              <div className="p-3 rounded-lg bg-muted/50 text-center">
                <div className="text-2xl font-bold text-purple-500">{aiAnswerStats.sessions?.today_total || 0}</div>
                <div className="text-xs text-muted-foreground">Sessions Today</div>
              </div>
            </div>
            
            {/* Routing Info */}
            <div className="mt-4 pt-4 border-t flex items-center justify-between text-sm">
              <div className="flex items-center gap-4">
                <div className="flex items-center gap-2">
                  <Shield className="h-4 w-4 text-muted-foreground" />
                  <span className="text-muted-foreground">Routing:</span>
                  <Badge variant="outline">
                    {aiAnswerStats.routing?.human_first ? 'Human First' : 'AI First'}
                  </Badge>
                </div>
                <div className="flex items-center gap-2">
                  <Users className="h-4 w-4 text-muted-foreground" />
                  <span className="text-muted-foreground">Human Agents:</span>
                  <span className="font-medium">{aiAnswerStats.routing?.human_agents_available || 0}</span>
                </div>
              </div>
              <div className="text-xs text-muted-foreground">
                Strategy: {aiAnswerStats.routing?.routing_strategy?.replace('_', ' ') || 'Round Robin'}
              </div>
            </div>
          </CardContent>
        </Card>
      )}
      
      {/* Active Calls Section */}
      <Card>
        <CardHeader className="pb-3">
          <CardTitle className="text-lg flex items-center gap-2">
            <Activity className="h-5 w-5 text-green-500" />
            Active Calls
            <Badge variant="outline" className="ml-2 bg-green-500/10 text-green-400 border-green-500/30">
              {activeCalls?.length || 0} live
            </Badge>
          </CardTitle>
        </CardHeader>
        <CardContent>
          {activeCalls?.length > 0 ? (
            <div className="grid grid-cols-2 gap-3">
              {activeCalls.map((call) => (
                <ActiveCallCard 
                  key={call.id} 
                  call={call}
                  onComplete={onCompleteCall}
                />
              ))}
            </div>
          ) : (
            <div className="text-center py-8 text-muted-foreground">
              <Phone className="h-10 w-10 mx-auto mb-2 opacity-50" />
              <p className="text-sm">No active calls</p>
            </div>
          )}
        </CardContent>
      </Card>
      
      {/* Quick Actions */}
      <Card>
        <CardHeader className="pb-3">
          <CardTitle className="text-lg">Quick Actions</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-3 gap-3">
            <Button variant="outline" className="h-auto py-4 flex-col">
              <PhoneIncoming className="h-6 w-6 mb-2" />
              <span className="text-sm">New Callback</span>
            </Button>
            <Button variant="outline" className="h-auto py-4 flex-col">
              <FileText className="h-6 w-6 mb-2" />
              <span className="text-sm">View Scripts</span>
            </Button>
            <Button variant="outline" className="h-auto py-4 flex-col">
              <Bell className="h-6 w-6 mb-2" />
              <span className="text-sm">Announcements</span>
            </Button>
          </div>
        </CardContent>
      </Card>
    </div>
  );
};

// ==========================================
// Time Slots Tab Content
// ==========================================
const TimeSlotsTabContent = ({ onSimulateOpen }) => {
  const [selectedDate, setSelectedDate] = useState(new Date().toISOString().split('T')[0]);
  const [slotsData, setSlotsData] = useState(null);
  const [loadingSlots, setLoadingSlots] = useState(false);
  const [businessHoursFilter, setBusinessHoursFilter] = useState(true);
  
  const days = useMemo(() => getNext7Days(), []);
  
  const fetchSlots = useCallback(async (date) => {
    setLoadingSlots(true);
    try {
      const response = await axios.get(`${API}/discovery/available-slots`, {
        params: { date }
      });
      setSlotsData(response.data);
    } catch (error) {
      console.error('Error fetching slots:', error);
    } finally {
      setLoadingSlots(false);
    }
  }, []);
  
  useEffect(() => {
    fetchSlots(selectedDate);
  }, [selectedDate, fetchSlots]);
  
  const handleDateChange = (date) => {
    setSelectedDate(date);
  };
  
  const filteredSlots = useMemo(() => {
    if (!slotsData?.slots) return [];
    return slotsData.slots;
  }, [slotsData?.slots]);
  
  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-2">
          <Switch 
            checked={businessHoursFilter} 
            onCheckedChange={setBusinessHoursFilter}
            id="bh-filter"
          />
          <Label htmlFor="bh-filter" className="text-sm">
            Business hours only
          </Label>
        </div>
        <Button variant="outline" size="sm" onClick={() => fetchSlots(selectedDate)}>
          <RefreshCw className="h-4 w-4 mr-2" />
          Refresh
        </Button>
      </div>
      
      {/* Date Tabs */}
      <div className="flex gap-1 overflow-x-auto pb-1">
        {days.map((day) => (
          <Button
            key={day.date}
            variant={selectedDate === day.date ? 'default' : 'outline'}
            size="sm"
            className="flex-shrink-0"
            onClick={() => handleDateChange(day.date)}
          >
            {day.label}
          </Button>
        ))}
      </div>
      
      {/* Slots Grid */}
      {loadingSlots ? (
        <div className="flex items-center justify-center h-64">
          <RefreshCw className="h-6 w-6 animate-spin text-muted-foreground" />
        </div>
      ) : filteredSlots.length > 0 ? (
        <div className="grid grid-cols-3 gap-3 max-h-[400px] overflow-y-auto pr-2">
          {filteredSlots.map((slot, i) => (
            <SlotCard 
              key={i} 
              slot={slot}
              onSimulate={onSimulateOpen}
            />
          ))}
        </div>
      ) : (
        <div className="text-center py-16 text-muted-foreground">
          <Calendar className="h-12 w-12 mx-auto mb-3 opacity-50" />
          <p>No available slots for this date</p>
          <p className="text-xs mt-1">
            {selectedDate === new Date().toISOString().split('T')[0] 
              ? 'Slots may have passed or cutoff time reached'
              : 'Check business hours configuration'}
          </p>
        </div>
      )}
    </div>
  );
};

// ==========================================
// Callbacks Tab Content
// ==========================================
const CallbacksTabContent = () => {
  const [callbacks, setCallbacks] = useState([]);
  const [loading, setLoading] = useState(true);
  const [statusFilter, setStatusFilter] = useState('all');
  const [repFilter, setRepFilter] = useState('all');
  
  const fetchCallbacks = useCallback(async () => {
    setLoading(true);
    try {
      const params = {};
      if (statusFilter !== 'all') params.status = statusFilter;
      if (repFilter !== 'all') params.assigned_to = repFilter;
      
      const response = await axios.get(`${API}/call-center/callbacks`, { params });
      setCallbacks(response.data.callbacks || []);
    } catch (error) {
      console.error('Error fetching callbacks:', error);
      toast.error('Failed to load callbacks');
    } finally {
      setLoading(false);
    }
  }, [statusFilter, repFilter]);
  
  useEffect(() => {
    fetchCallbacks();
  }, [fetchCallbacks]);
  
  const handleMarkDone = async (callbackId) => {
    try {
      await axios.patch(`${API}/call-center/callbacks/${callbackId}`, { status: 'completed' });
      toast.success('Callback marked as done');
      fetchCallbacks();
    } catch (error) {
      toast.error('Failed to update callback');
    }
  };
  
  const handleSnooze = async (callbackId) => {
    try {
      await axios.patch(`${API}/call-center/callbacks/${callbackId}`, { status: 'snoozed' });
      toast.success('Callback snoozed');
      fetchCallbacks();
    } catch (error) {
      toast.error('Failed to snooze callback');
    }
  };
  
  return (
    <div className="space-y-4">
      {/* Filters */}
      <div className="flex items-center gap-4">
        <div className="flex items-center gap-2">
          <Label className="text-sm">Status:</Label>
          <Select value={statusFilter} onValueChange={setStatusFilter}>
            <SelectTrigger className="w-32">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All</SelectItem>
              <SelectItem value="pending">Open</SelectItem>
              <SelectItem value="completed">Done</SelectItem>
              <SelectItem value="snoozed">Snoozed</SelectItem>
            </SelectContent>
          </Select>
        </div>
        
        <Button variant="outline" size="sm" onClick={fetchCallbacks}>
          <RefreshCw className="h-4 w-4 mr-2" />
          Refresh
        </Button>
        
        <Button size="sm" className="ml-auto">
          <Plus className="h-4 w-4 mr-2" />
          New Callback
        </Button>
      </div>
      
      {/* Callbacks Table */}
      {loading ? (
        <div className="flex items-center justify-center h-64">
          <RefreshCw className="h-6 w-6 animate-spin text-muted-foreground" />
        </div>
      ) : callbacks.length > 0 ? (
        <div className="rounded-md border">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Due</TableHead>
                <TableHead>Customer</TableHead>
                <TableHead>Phone</TableHead>
                <TableHead>Assigned Rep</TableHead>
                <TableHead>Notes</TableHead>
                <TableHead>Status</TableHead>
                <TableHead className="text-right">Actions</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {callbacks.map((cb) => (
                <TableRow key={cb.id}>
                  <TableCell className="text-sm">
                    {formatDateTime(cb.scheduled_at)}
                  </TableCell>
                  <TableCell className="font-medium">{cb.contact_name || 'Unknown'}</TableCell>
                  <TableCell>{cb.phone_number}</TableCell>
                  <TableCell>{cb.assigned_to_name || cb.assigned_to || '-'}</TableCell>
                  <TableCell className="max-w-[200px] truncate text-muted-foreground text-sm">
                    {cb.notes || cb.reason || '-'}
                  </TableCell>
                  <TableCell>
                    <Badge variant={cb.status === 'completed' ? 'default' : cb.status === 'snoozed' ? 'secondary' : 'outline'}>
                      {cb.status}
                    </Badge>
                  </TableCell>
                  <TableCell className="text-right">
                    <div className="flex items-center justify-end gap-1">
                      <Button size="sm" variant="ghost" onClick={() => handleMarkDone(cb.id)}>
                        <CheckCircle className="h-4 w-4" />
                      </Button>
                      <Button size="sm" variant="ghost" onClick={() => handleSnooze(cb.id)}>
                        <Clock className="h-4 w-4" />
                      </Button>
                      <Button size="sm" variant="ghost">
                        <ExternalLink className="h-4 w-4" />
                      </Button>
                    </div>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </div>
      ) : (
        <div className="text-center py-16 text-muted-foreground">
          <PhoneIncoming className="h-12 w-12 mx-auto mb-3 opacity-50" />
          <p>No callbacks found</p>
          <p className="text-xs mt-1">Schedule a callback to get started</p>
        </div>
      )}
    </div>
  );
};

// ==========================================
// Call Log Tab Content - Full Featured
// ==========================================
const callLogStatusConfig = {
  completed: { label: 'Answered', color: 'bg-green-100 text-green-800 dark:bg-green-900/30 dark:text-green-300' },
  missed: { label: 'Missed', color: 'bg-red-100 text-red-800 dark:bg-red-900/30 dark:text-red-300' },
  voicemail: { label: 'Voicemail', color: 'bg-purple-100 text-purple-800 dark:bg-purple-900/30 dark:text-purple-300' },
  abandoned: { label: 'Abandoned', color: 'bg-orange-100 text-orange-800 dark:bg-orange-900/30 dark:text-orange-300' },
  busy: { label: 'Busy', color: 'bg-yellow-100 text-yellow-800 dark:bg-yellow-900/30 dark:text-yellow-300' }
};

const regionConfig = {
  JAX: { name: 'Jacksonville', color: 'bg-blue-100 text-blue-800 dark:bg-blue-900/30 dark:text-blue-300' },
  ORL: { name: 'Orlando', color: 'bg-purple-100 text-purple-800 dark:bg-purple-900/30 dark:text-purple-300' },
  TAM: { name: 'Tampa', color: 'bg-green-100 text-green-800 dark:bg-green-900/30 dark:text-green-300' },
  WPB: { name: 'West Palm Beach', color: 'bg-orange-100 text-orange-800 dark:bg-orange-900/30 dark:text-orange-300' },
  NAP: { name: 'Naples', color: 'bg-cyan-100 text-cyan-800 dark:bg-cyan-900/30 dark:text-cyan-300' },
  MIA: { name: 'Miami', color: 'bg-pink-100 text-pink-800 dark:bg-pink-900/30 dark:text-pink-300' }
};

const CallLogTabContent = () => {
  const [calls, setCalls] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [regionFilter, setRegionFilter] = useState('all');
  const [directionFilter, setDirectionFilter] = useState('all');
  const [selectedCall, setSelectedCall] = useState(null);
  const [page, setPage] = useState(1);
  const [totalCalls, setTotalCalls] = useState(0);
  
  const fetchCalls = useCallback(async () => {
    setLoading(true);
    try {
      const token = localStorage.getItem('access_token');
      const params = new URLSearchParams();
      params.append('page', page);
      params.append('page_size', 50);
      
      if (statusFilter !== 'all') params.append('status', statusFilter);
      if (regionFilter !== 'all') params.append('region', regionFilter);
      if (directionFilter !== 'all') params.append('direction', directionFilter);
      
      const response = await axios.get(`${API}/call-center/interactions?${params}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      setCalls(response.data.interactions || []);
      setTotalCalls(response.data.total || 0);
    } catch (error) {
      console.error('Error fetching call log:', error);
      toast.error('Failed to load call log');
    } finally {
      setLoading(false);
    }
  }, [page, statusFilter, regionFilter, directionFilter]);
  
  useEffect(() => {
    fetchCalls();
  }, [fetchCalls]);

  const filteredCalls = calls.filter(call => {
    if (!searchTerm) return true;
    const search = searchTerm.toLowerCase();
    return (
      call.caller_number?.includes(search) ||
      call.caller_name?.toLowerCase().includes(search) ||
      call.contact_name?.toLowerCase().includes(search) ||
      call.tracking_number_name?.toLowerCase().includes(search)
    );
  });

  const formatDuration = (seconds) => {
    if (!seconds) return '-';
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${mins}:${secs.toString().padStart(2, '0')}`;
  };

  const getCallIcon = (call) => {
    if (call.status === 'missed') return PhoneMissed;
    if (call.direction === 'outbound') return Phone;
    return PhoneIncoming;
  };
  
  return (
    <div className="space-y-4">
      {/* Header with stats */}
      <div className="flex items-center justify-between">
        <div className="text-sm text-muted-foreground">
          {totalCalls} total calls
          {calls.some(c => c.is_backfill) && <Badge variant="outline" className="ml-2 text-xs">Includes imported data</Badge>}
        </div>
        <Button variant="outline" size="sm" onClick={fetchCalls}>
          <RefreshCw className={`h-4 w-4 mr-2 ${loading ? 'animate-spin' : ''}`} />
          Refresh
        </Button>
      </div>

      {/* Filters */}
      <div className="flex flex-wrap items-center gap-3">
        <div className="flex-1 min-w-[200px] max-w-sm">
          <div className="relative">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
            <Input
              placeholder="Search by name or number..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="pl-10"
            />
          </div>
        </div>
        
        <Select value={directionFilter} onValueChange={setDirectionFilter}>
          <SelectTrigger className="w-[130px]">
            <SelectValue placeholder="Direction" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All Calls</SelectItem>
            <SelectItem value="inbound">Inbound</SelectItem>
            <SelectItem value="outbound">Outbound</SelectItem>
          </SelectContent>
        </Select>

        <Select value={statusFilter} onValueChange={setStatusFilter}>
          <SelectTrigger className="w-[130px]">
            <SelectValue placeholder="Status" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All Status</SelectItem>
            <SelectItem value="completed">Answered</SelectItem>
            <SelectItem value="missed">Missed</SelectItem>
            <SelectItem value="voicemail">Voicemail</SelectItem>
            <SelectItem value="abandoned">Abandoned</SelectItem>
          </SelectContent>
        </Select>

        <Select value={regionFilter} onValueChange={setRegionFilter}>
          <SelectTrigger className="w-[150px]">
            <SelectValue placeholder="Region" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All Regions</SelectItem>
            {Object.entries(regionConfig).map(([code, config]) => (
              <SelectItem key={code} value={code}>{config.name}</SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>
      
      {/* Call Log List */}
      {loading ? (
        <div className="flex items-center justify-center h-64">
          <RefreshCw className="h-6 w-6 animate-spin text-muted-foreground" />
        </div>
      ) : filteredCalls.length > 0 ? (
        <div className="space-y-2">
          {filteredCalls.map((call) => {
            const Icon = getCallIcon(call);
            const status = callLogStatusConfig[call.status] || callLogStatusConfig.completed;
            const region = regionConfig[call.region];
            
            return (
              <div
                key={call.id}
                onClick={() => setSelectedCall(call)}
                className="flex items-center gap-4 p-3 border rounded-lg hover:bg-accent cursor-pointer transition-colors"
              >
                <div className={`p-2 rounded-full ${call.status === 'missed' ? 'bg-red-100 dark:bg-red-900/30' : call.direction === 'inbound' ? 'bg-green-100 dark:bg-green-900/30' : 'bg-blue-100 dark:bg-blue-900/30'}`}>
                  <Icon className={`h-4 w-4 ${call.status === 'missed' ? 'text-red-600 dark:text-red-400' : call.direction === 'inbound' ? 'text-green-600 dark:text-green-400' : 'text-blue-600 dark:text-blue-400'}`} />
                </div>
                
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2">
                    <span className="font-medium text-sm truncate">
                      {call.caller_name || call.contact_name || call.caller_number || 'Unknown'}
                    </span>
                    {call.is_backfill && (
                      <Badge variant="outline" className="text-[10px]">Imported</Badge>
                    )}
                  </div>
                  <div className="flex items-center gap-2 text-xs text-muted-foreground mt-0.5">
                    <span>{call.caller_number}</span>
                    {call.tracking_number_name && (
                      <>
                        <span>•</span>
                        <span className="truncate max-w-[150px]">{call.tracking_number_name}</span>
                      </>
                    )}
                  </div>
                </div>

                <div className="flex items-center gap-2">
                  {region && (
                    <Badge variant="outline" className={`text-xs ${region.color}`}>
                      {call.region}
                    </Badge>
                  )}
                </div>

                <div className="text-right">
                  <Badge className={`text-xs ${status.color}`}>
                    {status.label}
                  </Badge>
                  <div className="text-[10px] text-muted-foreground mt-0.5">
                    {formatDateTime(call.started_at)}
                  </div>
                </div>

                <div className="text-right text-sm w-14">
                  <div className="font-medium text-xs">{formatDuration(call.duration_seconds)}</div>
                  {call.recording_url && (
                    <Play className="h-3 w-3 text-muted-foreground mx-auto mt-0.5" />
                  )}
                </div>
              </div>
            );
          })}
        </div>
      ) : (
        <div className="text-center py-16 text-muted-foreground">
          <ScrollText className="h-12 w-12 mx-auto mb-3 opacity-50" />
          <p>No call records found</p>
          <p className="text-xs mt-1">Call history will appear here</p>
        </div>
      )}

      {/* Pagination */}
      {totalCalls > 50 && (
        <div className="flex justify-center items-center gap-2 pt-4 border-t">
          <Button
            variant="outline"
            size="sm"
            disabled={page === 1}
            onClick={() => setPage(p => p - 1)}
          >
            Previous
          </Button>
          <span className="text-sm text-muted-foreground px-4">
            Page {page} of {Math.ceil(totalCalls / 50)}
          </span>
          <Button
            variant="outline"
            size="sm"
            disabled={page >= Math.ceil(totalCalls / 50)}
            onClick={() => setPage(p => p + 1)}
          >
            Next
          </Button>
        </div>
      )}

      {/* Call Detail Modal */}
      <Dialog open={!!selectedCall} onOpenChange={() => setSelectedCall(null)}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Phone className="h-5 w-5" />
              Call Details
            </DialogTitle>
          </DialogHeader>
          {selectedCall && (
            <div className="space-y-4">
              <div className="grid grid-cols-2 gap-4 text-sm">
                <div>
                  <Label className="text-muted-foreground">Caller</Label>
                  <p className="font-medium">{selectedCall.caller_name || selectedCall.caller_number || 'Unknown'}</p>
                </div>
                <div>
                  <Label className="text-muted-foreground">Phone Number</Label>
                  <p className="font-medium">{selectedCall.caller_number || '-'}</p>
                </div>
                <div>
                  <Label className="text-muted-foreground">Direction</Label>
                  <p className="font-medium capitalize">{selectedCall.direction || 'Inbound'}</p>
                </div>
                <div>
                  <Label className="text-muted-foreground">Status</Label>
                  <Badge className={callLogStatusConfig[selectedCall.status]?.color || ''}>
                    {callLogStatusConfig[selectedCall.status]?.label || selectedCall.status}
                  </Badge>
                </div>
                <div>
                  <Label className="text-muted-foreground">Duration</Label>
                  <p className="font-medium">{formatDuration(selectedCall.duration_seconds)}</p>
                </div>
                <div>
                  <Label className="text-muted-foreground">Date/Time</Label>
                  <p className="font-medium">{formatDateTime(selectedCall.started_at)}</p>
                </div>
                {selectedCall.tracking_number_name && (
                  <div className="col-span-2">
                    <Label className="text-muted-foreground">Tracking Source</Label>
                    <p className="font-medium">{selectedCall.tracking_number_name}</p>
                  </div>
                )}
                {selectedCall.region && (
                  <div>
                    <Label className="text-muted-foreground">Region</Label>
                    <Badge variant="outline" className={regionConfig[selectedCall.region]?.color}>
                      {regionConfig[selectedCall.region]?.name || selectedCall.region}
                    </Badge>
                  </div>
                )}
              </div>
              
              {selectedCall.recording_url && (
                <div className="pt-2 border-t">
                  <Label className="text-muted-foreground">Recording</Label>
                  <audio controls className="w-full mt-2">
                    <source src={selectedCall.recording_url} type="audio/mpeg" />
                  </audio>
                </div>
              )}
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
};

// ==========================================
// Scripts Tab Content
// ==========================================
const ScriptsTabContent = () => {
  const { user } = useAuth();
  const [scripts, setScripts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedScript, setSelectedScript] = useState(null);
  const [editMode, setEditMode] = useState(false);
  const [editForm, setEditForm] = useState({ name: '', script_type: 'call_script', content: '' });
  const [saving, setSaving] = useState(false);
  
  const isAdmin = user?.role === 'super_admin' || user?.role === 'admin';
  
  const fetchScripts = useCallback(async () => {
    setLoading(true);
    try {
      const response = await axios.get(`${API}/call-center/scripts`);
      setScripts(response.data.scripts || []);
      if (response.data.scripts?.length > 0 && !selectedScript) {
        setSelectedScript(response.data.scripts[0]);
      }
    } catch (error) {
      console.error('Error fetching scripts:', error);
      toast.error('Failed to load scripts');
    } finally {
      setLoading(false);
    }
  }, [selectedScript]);
  
  useEffect(() => {
    fetchScripts();
  }, []);
  
  const handleNewScript = () => {
    setSelectedScript(null);
    setEditForm({ name: '', script_type: 'call_script', content: '' });
    setEditMode(true);
  };
  
  const handleEditScript = () => {
    if (selectedScript) {
      setEditForm({
        name: selectedScript.name,
        script_type: selectedScript.script_type || 'call_script',
        content: selectedScript.content || ''
      });
      setEditMode(true);
    }
  };
  
  const handleSaveScript = async () => {
    if (!editForm.name.trim()) {
      toast.error('Script name is required');
      return;
    }
    
    setSaving(true);
    try {
      if (selectedScript?.id) {
        await axios.patch(`${API}/call-center/scripts/${selectedScript.id}`, editForm);
        toast.success('Script updated');
      } else {
        await axios.post(`${API}/call-center/scripts`, editForm);
        toast.success('Script created');
      }
      setEditMode(false);
      fetchScripts();
    } catch (error) {
      toast.error('Failed to save script');
    } finally {
      setSaving(false);
    }
  };
  
  const handleArchiveScript = async () => {
    if (!selectedScript?.id) return;
    
    try {
      await axios.patch(`${API}/call-center/scripts/${selectedScript.id}`, { is_published: false });
      toast.success('Script archived');
      setSelectedScript(null);
      fetchScripts();
    } catch (error) {
      toast.error('Failed to archive script');
    }
  };
  
  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <RefreshCw className="h-6 w-6 animate-spin text-muted-foreground" />
      </div>
    );
  }
  
  return (
    <div className="flex h-[500px] gap-4">
      {/* Scripts List */}
      <div className="w-64 border rounded-lg flex flex-col">
        <div className="p-3 border-b flex items-center justify-between">
          <h3 className="font-medium text-sm">Scripts</h3>
          {isAdmin && (
            <Button size="sm" variant="ghost" onClick={handleNewScript}>
              <Plus className="h-4 w-4" />
            </Button>
          )}
        </div>
        <ScrollArea className="flex-1">
          <div className="p-2 space-y-1">
            {scripts.map((script) => (
              <button
                key={script.id}
                onClick={() => { setSelectedScript(script); setEditMode(false); }}
                className={`w-full text-left p-2 rounded-lg text-sm transition-colors ${
                  selectedScript?.id === script.id 
                    ? 'bg-primary text-primary-foreground' 
                    : 'hover:bg-muted'
                }`}
              >
                <div className="font-medium truncate">{script.name}</div>
                <div className="text-xs opacity-70">{script.script_type}</div>
              </button>
            ))}
            {scripts.length === 0 && (
              <div className="text-center py-8 text-muted-foreground text-sm">
                No scripts yet
              </div>
            )}
          </div>
        </ScrollArea>
      </div>
      
      {/* Script Editor/Viewer */}
      <div className="flex-1 border rounded-lg flex flex-col">
        {editMode ? (
          <>
            <div className="p-3 border-b">
              <Input
                placeholder="Script Name"
                value={editForm.name}
                onChange={(e) => setEditForm({ ...editForm, name: e.target.value })}
                className="font-medium"
              />
              <Select 
                value={editForm.script_type} 
                onValueChange={(v) => setEditForm({ ...editForm, script_type: v })}
              >
                <SelectTrigger className="mt-2">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="call_script">Call Script</SelectItem>
                  <SelectItem value="objection_handling">Objection Handling</SelectItem>
                  <SelectItem value="voicemail">Voicemail</SelectItem>
                  <SelectItem value="email_template">Email Template</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div className="flex-1 p-3">
              <Textarea
                placeholder="Enter script content..."
                value={editForm.content}
                onChange={(e) => setEditForm({ ...editForm, content: e.target.value })}
                className="h-full min-h-[300px] resize-none"
              />
            </div>
            <div className="p-3 border-t flex items-center justify-end gap-2">
              <Button variant="outline" onClick={() => setEditMode(false)}>
                Cancel
              </Button>
              <Button onClick={handleSaveScript} disabled={saving}>
                {saving ? <RefreshCw className="h-4 w-4 mr-2 animate-spin" /> : null}
                Save
              </Button>
            </div>
          </>
        ) : selectedScript ? (
          <>
            <div className="p-3 border-b flex items-center justify-between">
              <div>
                <h3 className="font-medium">{selectedScript.name}</h3>
                <p className="text-xs text-muted-foreground">{selectedScript.script_type}</p>
              </div>
              {isAdmin && (
                <div className="flex items-center gap-1">
                  <Button size="sm" variant="ghost" onClick={handleEditScript}>
                    <Edit className="h-4 w-4" />
                  </Button>
                  <Button size="sm" variant="ghost" onClick={handleArchiveScript}>
                    <Archive className="h-4 w-4" />
                  </Button>
                </div>
              )}
            </div>
            <ScrollArea className="flex-1 p-4">
              <div className="prose prose-sm dark:prose-invert max-w-none whitespace-pre-wrap">
                {selectedScript.content || 'No content'}
              </div>
            </ScrollArea>
          </>
        ) : (
          <div className="flex-1 flex items-center justify-center text-muted-foreground">
            <div className="text-center">
              <FileText className="h-12 w-12 mx-auto mb-3 opacity-50" />
              <p>Select a script to view</p>
              {isAdmin && (
                <Button variant="link" onClick={handleNewScript}>
                  or create a new one
                </Button>
              )}
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

// ==========================================
// Main Component
// ==========================================
const ControlPanel = () => {
  const { user } = useAuth();
  const [searchParams, setSearchParams] = useSearchParams();
  
  // Get tab from URL or default to 'dashboard'
  const activeTab = searchParams.get('tab') || 'dashboard';
  
  // State
  const [loading, setLoading] = useState(true);
  const [autoRefresh, setAutoRefresh] = useState(true);
  const [controlPanelData, setControlPanelData] = useState(null);
  const [aiAnswerStats, setAiAnswerStats] = useState(null);
  const [simulateDialogOpen, setSimulateDialogOpen] = useState(false);
  const [settingsOpen, setSettingsOpen] = useState(false);
  const [workStationOpen, setWorkStationOpen] = useState(false);
  const [popupBlocked, setPopupBlocked] = useState(false);
  
  // Open Work Station in new window
  const openWorkStation = () => {
    const url = `${window.location.origin}/call-center/work-station`;
    const newWindow = window.open(
      url,
      'CallCenterWorkStation',
      'noopener,noreferrer,width=1280,height=800'
    );
    
    if (!newWindow || newWindow.closed || typeof newWindow.closed === 'undefined') {
      setPopupBlocked(true);
      toast.error('Popup blocked! Please allow popups for this site.');
    } else {
      setWorkStationOpen(true);
      toast.success('Work Station opened in new window');
      
      // Check if window is closed periodically
      const checkClosed = setInterval(() => {
        if (newWindow.closed) {
          setWorkStationOpen(false);
          clearInterval(checkClosed);
        }
      }, 2000);
    }
  };
  
  // Fetch control panel data
  const fetchControlPanel = useCallback(async () => {
    try {
      const token = localStorage.getItem('access_token');
      const headers = { Authorization: `Bearer ${token}` };
      
      const [cpResponse, aiResponse] = await Promise.all([
        axios.get(`${API}/discovery/control-panel`),
        axios.get(`${API}/call-center/ai-answer/stats`, { headers }).catch(() => ({ data: null }))
      ]);
      
      setControlPanelData(cpResponse.data);
      setAiAnswerStats(aiResponse.data);
    } catch (error) {
      console.error('Error fetching control panel:', error);
    } finally {
      setLoading(false);
    }
  }, []);
  
  // Initial load
  useEffect(() => {
    fetchControlPanel();
  }, [fetchControlPanel]);
  
  // Auto-refresh polling
  useEffect(() => {
    if (!autoRefresh) return;
    
    const interval = setInterval(() => {
      fetchControlPanel();
    }, 5000);
    
    return () => clearInterval(interval);
  }, [autoRefresh, fetchControlPanel]);
  
  // Handle tab change
  const handleTabChange = (tab) => {
    setSearchParams({ tab });
  };
  
  // Handle rep status change
  const handleRepStatusChange = async (repId, newStatus) => {
    try {
      await axios.post(`${API}/discovery/toggle-rep-availability`, {
        rep_user_id: repId,
        status: newStatus
      });
      toast.success(`Rep status updated to ${STATUS_LABELS[newStatus]}`);
      fetchControlPanel();
    } catch (error) {
      console.error('Error updating rep status:', error);
      toast.error('Failed to update rep status');
    }
  };
  
  // Handle call completion
  const handleCompleteCall = async (bookingId) => {
    try {
      await axios.post(`${API}/discovery/end-call/${bookingId}`, null, {
        params: { outcome: 'Completed via Control Panel' }
      });
      toast.success('Call marked as complete');
      fetchControlPanel();
    } catch (error) {
      console.error('Error completing call:', error);
      toast.error('Failed to complete call');
    }
  };
  
  // Seed data
  const handleSeedData = async () => {
    try {
      const response = await axios.post(`${API}/discovery/seed`);
      toast.success(`Seeded ${response.data.users_created} test users`);
      fetchControlPanel();
    } catch (error) {
      console.error('Error seeding data:', error);
      toast.error('Failed to seed data');
    }
  };
  
  // Calculate next rep in queue
  const nextRepInQueue = useMemo(() => {
    if (!controlPanelData?.reps) return null;
    
    const availableReps = controlPanelData.reps
      .filter(r => r.status === 'available' && !r.current_call_id)
      .sort((a, b) => {
        const callsA = a.calls_handled_today || 0;
        const callsB = b.calls_handled_today || 0;
        if (callsA !== callsB) return callsA - callsB;
        return (a.last_call_ended_at || '') < (b.last_call_ended_at || '') ? -1 : 1;
      });
    
    return availableReps[0]?.user_id || null;
  }, [controlPanelData?.reps]);
  
  if (loading) {
    return (
      <Layout>
        <div className="flex items-center justify-center h-96">
          <RefreshCw className="h-8 w-8 animate-spin text-muted-foreground" />
        </div>
      </Layout>
    );
  }
  
  const stats = controlPanelData || {
    total_reps: 0,
    available_reps: 0,
    on_call_reps: 0,
    booked_slots_today: 0,
    completed_calls_today: 0,
    reps: [],
    active_calls: []
  };

  return (
    <Layout>
      <div className="space-y-6">
        {/* Header */}
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-2xl font-bold flex items-center gap-2">
              <Radio className="h-6 w-6 text-primary" />
              Control Panel
            </h1>
            <p className="text-muted-foreground text-sm">
              Manage Discovery Call capacity and routing in real-time
            </p>
          </div>
          
          <div className="flex items-center gap-2">
            {/* Work Station Button */}
            <Button 
              variant={workStationOpen ? 'default' : 'outline'}
              onClick={openWorkStation}
              className={workStationOpen ? 'bg-green-600 hover:bg-green-700' : ''}
            >
              <Monitor className="h-4 w-4 mr-2" />
              Work Station
              {workStationOpen && (
                <Badge variant="secondary" className="ml-2 bg-white/20 text-white text-xs">
                  Open
                </Badge>
              )}
            </Button>
            
            {popupBlocked && (
              <div className="text-xs text-red-500 bg-red-500/10 px-2 py-1 rounded">
                Popup blocked - allow popups
              </div>
            )}
            
            <div className="flex items-center gap-2 bg-muted/50 rounded-lg px-3 py-1.5">
              <Switch 
                checked={autoRefresh} 
                onCheckedChange={setAutoRefresh}
                id="auto-refresh"
              />
              <Label htmlFor="auto-refresh" className="text-sm cursor-pointer">
                Auto-refresh
              </Label>
              {autoRefresh && (
                <RefreshCw className="h-3 w-3 text-green-500 animate-spin" />
              )}
            </div>
            
            <Button variant="outline" onClick={() => setSimulateDialogOpen(true)}>
              <Zap className="h-4 w-4 mr-2" />
              Simulate Call
            </Button>
            
            <Button variant="outline" onClick={() => setSettingsOpen(true)}>
              <Settings className="h-4 w-4" />
            </Button>
            
            <Button variant="ghost" size="icon" onClick={fetchControlPanel}>
              <RefreshCw className={`h-4 w-4 ${loading ? 'animate-spin' : ''}`} />
            </Button>
          </div>
        </div>
        
        {/* Stats Cards Row */}
        <div className="grid grid-cols-5 gap-4">
          <Card>
            <CardContent className="p-4">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-muted-foreground">Total Reps</p>
                  <p className="text-2xl font-bold">{stats.total_reps}</p>
                </div>
                <Users className="h-8 w-8 text-muted-foreground/50" />
              </div>
            </CardContent>
          </Card>
          
          <Card className="bg-green-500/5 border-green-500/20">
            <CardContent className="p-4">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-green-400">Available</p>
                  <p className="text-2xl font-bold text-green-500">{stats.available_reps}</p>
                </div>
                <UserCheck className="h-8 w-8 text-green-500/50" />
              </div>
            </CardContent>
          </Card>
          
          <Card className="bg-blue-500/5 border-blue-500/20">
            <CardContent className="p-4">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-blue-400">On Call</p>
                  <p className="text-2xl font-bold text-blue-500">{stats.on_call_reps}</p>
                </div>
                <PhoneCall className="h-8 w-8 text-blue-500/50" />
              </div>
            </CardContent>
          </Card>
          
          <Card>
            <CardContent className="p-4">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-muted-foreground">Booked Today</p>
                  <p className="text-2xl font-bold">{stats.booked_slots_today}</p>
                </div>
                <Calendar className="h-8 w-8 text-muted-foreground/50" />
              </div>
            </CardContent>
          </Card>
          
          <Card>
            <CardContent className="p-4">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-muted-foreground">Completed</p>
                  <p className="text-2xl font-bold text-green-500">{stats.completed_calls_today}</p>
                </div>
                <CheckCircle className="h-8 w-8 text-green-500/50" />
              </div>
            </CardContent>
          </Card>
        </div>
        
        {/* Main Content - Two Column Layout */}
        <div className="grid grid-cols-12 gap-6">
          {/* Left Column - Discovery Agents */}
          <div className="col-span-4">
            <Card className="h-full">
              <CardHeader className="pb-3">
                <div className="flex items-center justify-between">
                  <div>
                    <CardTitle className="text-lg flex items-center gap-2">
                      <Headphones className="h-5 w-5" />
                      Discovery Agents
                    </CardTitle>
                    <CardDescription>
                      Quick toggle availability and view call states
                    </CardDescription>
                  </div>
                  <Badge variant="outline">
                    {stats.available_reps}/{stats.total_reps} ready
                  </Badge>
                </div>
              </CardHeader>
              <CardContent className="space-y-3 max-h-[600px] overflow-y-auto">
                {stats.reps?.length > 0 ? (
                  stats.reps.map((rep) => (
                    <RepCard 
                      key={rep.user_id} 
                      rep={rep} 
                      onStatusChange={handleRepStatusChange}
                      isNextInQueue={rep.user_id === nextRepInQueue}
                    />
                  ))
                ) : (
                  <div className="text-center py-8 text-muted-foreground">
                    <Users className="h-12 w-12 mx-auto mb-3 opacity-50" />
                    <p>No discovery agents found</p>
                    <Button 
                      variant="link" 
                      className="mt-2"
                      onClick={handleSeedData}
                    >
                      Seed test agents
                    </Button>
                  </div>
                )}
              </CardContent>
            </Card>
          </div>
          
          {/* Right Column - Tabbed Content */}
          <div className="col-span-8">
            <Card className="h-full">
              <CardContent className="p-0">
                <Tabs value={activeTab} onValueChange={handleTabChange} className="h-full">
                  <div className="border-b px-4 pt-4">
                    <TabsList className="w-full justify-start h-auto p-1 bg-muted/50">
                      {TABS.map((tab) => {
                        const Icon = tab.icon;
                        return (
                          <TabsTrigger 
                            key={tab.id} 
                            value={tab.id}
                            className="flex items-center gap-2 data-[state=active]:bg-background"
                          >
                            <Icon className="h-4 w-4" />
                            {tab.label}
                          </TabsTrigger>
                        );
                      })}
                    </TabsList>
                  </div>
                  
                  <div className="p-4">
                    <TabsContent value="dashboard" className="mt-0">
                      <DashboardTabContent 
                        stats={stats} 
                        activeCalls={stats.active_calls}
                        onCompleteCall={handleCompleteCall}
                        aiAnswerStats={aiAnswerStats}
                      />
                    </TabsContent>
                    
                    <TabsContent value="time-slots" className="mt-0">
                      <TimeSlotsTabContent 
                        onSimulateOpen={() => setSimulateDialogOpen(true)}
                      />
                    </TabsContent>
                    
                    <TabsContent value="callbacks" className="mt-0">
                      <CallbacksTabContent />
                    </TabsContent>
                    
                    <TabsContent value="call-log" className="mt-0">
                      <CallLogTabContent />
                    </TabsContent>
                    
                    <TabsContent value="scripts" className="mt-0">
                      <ScriptsTabContent />
                    </TabsContent>
                  </div>
                </Tabs>
              </CardContent>
            </Card>
          </div>
        </div>
        
        {/* Simulate Call Dialog */}
        <SimulateCallDialog
          open={simulateDialogOpen}
          onOpenChange={setSimulateDialogOpen}
          slots={[]}
          onSimulate={fetchControlPanel}
        />
        
        {/* Settings Dialog */}
        <Dialog open={settingsOpen} onOpenChange={setSettingsOpen}>
          <DialogContent>
            <DialogHeader>
              <DialogTitle>Discovery Pool Settings</DialogTitle>
              <DialogDescription>
                Configure the Discovery Call pool parameters
              </DialogDescription>
            </DialogHeader>
            
            <div className="space-y-4 py-4">
              <div className="space-y-2">
                <Label>Pool Name</Label>
                <Input 
                  value={controlPanelData?.pool?.name || 'Inbound Discovery Calls'} 
                  disabled 
                />
              </div>
              
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label>Slot Duration</Label>
                  <Input 
                    value={`${controlPanelData?.pool?.slot_duration_minutes || 30} minutes`} 
                    disabled 
                  />
                </div>
                <div className="space-y-2">
                  <Label>Booking Cutoff</Label>
                  <Input 
                    value={`${controlPanelData?.pool?.booking_cutoff_minutes || 30} minutes before`} 
                    disabled 
                  />
                </div>
              </div>
              
              <Separator />
              
              <div className="space-y-2">
                <Label>Actions</Label>
                <div className="flex gap-2">
                  <Button variant="outline" onClick={handleSeedData}>
                    <Plus className="h-4 w-4 mr-2" />
                    Seed Test Data
                  </Button>
                </div>
              </div>
            </div>
            
            <DialogFooter>
              <Button variant="outline" onClick={() => setSettingsOpen(false)}>
                Close
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </div>
    </Layout>
  );
};

export default ControlPanel;
